import React, { useEffect, useMemo, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  FileText, Plus, X, Copy, Eye, EyeOff, GripVertical, ChevronUp, ChevronDown, ChevronsDownUp,
  LayoutDashboard, BarChart3, LineChart, PieChart, Table, Activity,
  TrendingUp, Map as MapIcon, Gauge, Users, DollarSign, Calendar, Home, Star,
} from 'lucide-react';

// Curated icon set an author can attach to a page.
export const PAGE_ICONS = {
  file: FileText, dashboard: LayoutDashboard, bar: BarChart3, line: LineChart,
  pie: PieChart, table: Table, activity: Activity, trend: TrendingUp,
  map: MapIcon, gauge: Gauge, users: Users, money: DollarSign,
  calendar: Calendar, home: Home, star: Star,
};
export const PAGE_ICON_KEYS = Object.keys(PAGE_ICONS);

export function PageIcon({ name, className = 'w-3.5 h-3.5' }) {
  const Cmp = PAGE_ICONS[name];
  if (!Cmp) return null;
  return <Cmp className={className} />;
}

// Author-selectable navigation styles for a multi-page dashboard.
export const NAV_STYLES = [
  { value: 'top_tabs', label: 'Top tabs' },
  { value: 'bottom_tabs', label: 'Bottom tabs' },
  { value: 'left_sidebar', label: 'Left sidebar' },
  { value: 'right_sidebar', label: 'Right sidebar' },
  { value: 'dropdown', label: 'Dropdown' },
];

// Legacy values used before Pages existed.
export function normalizeNavStyle(style) {
  if (style === 'tabs' || !style) return 'top_tabs';
  return style;
}

const pid = (p) => p.tab_id || p.id;
export const isPageVisible = (p) => p.visible !== false;

// Collapse tabs/sidebar to a dropdown when the container gets tight so
// navigation never overflows on smaller widths.
export function useNarrow(breakpoint = 720) {
  const [narrow, setNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return narrow;
}

// Read-only page navigation. `showHidden` (author/builder) renders hidden
// pages dimmed; viewers never receive hidden pages.
export function DashboardPageNav({ pages, activeId, onChange, style = 'top_tabs', showHidden = false, storageKey = '' }) {
  const s = normalizeNavStyle(style);
  const lsKey = storageKey ? `pyr-nav-collapsed:${storageKey}` : '';
  // Remember which sidebar sections a viewer collapsed (per-dashboard, per-browser).
  const [collapsed, setCollapsed] = useState(() => {
    if (!lsKey || typeof window === 'undefined') return {};
    try { return JSON.parse(window.localStorage.getItem(lsKey) || '{}') || {}; } catch { return {}; }
  });
  const toggleGroup = (g) => setCollapsed((c) => {
    const next = { ...c, [g]: !c[g] };
    if (lsKey) { try { window.localStorage.setItem(lsKey, JSON.stringify(next)); } catch { /* ignore quota */ } }
    return next;
  });
  const setAllGroups = (groups, val) => setCollapsed(() => {
    const next = {};
    if (val) groups.forEach((g) => { next[g] = true; });
    if (lsKey) { try { window.localStorage.setItem(lsKey, JSON.stringify(next)); } catch { /* ignore quota */ } }
    return next;
  });
  if (!pages || pages.length <= 1) return null;

  if (s === 'dropdown') {
    return (
      <div className="mb-3" data-testid="page-nav-dropdown">
        <Select value={activeId} onValueChange={onChange}>
          <SelectTrigger className="w-56 h-8 text-xs rounded-lg bg-muted/30 border-border/30">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pages.map((pg) => (
              <SelectItem key={pid(pg)} value={pid(pg)}>
                {pg.name}{showHidden && !isPageVisible(pg) ? ' (hidden)' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  const vertical = s === 'left_sidebar' || s === 'right_sidebar';
  const renderBtn = (pg) => {
    const active = activeId === pid(pg);
    const hidden = showHidden && !isPageVisible(pg);
    return (
      <button
        key={pid(pg)}
        onClick={() => onChange(pid(pg))}
        data-testid={`page-nav-item-${pid(pg)}`}
        aria-current={active ? 'page' : undefined}
        className={[
          vertical
            ? 'flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-left w-full transition-colors'
            : 'px-3 py-1.5 rounded-t-lg text-xs transition-colors',
          active
            ? 'bg-primary/15 text-primary font-semibold ' + (vertical ? 'border border-primary/25' : 'border-b-2 border-primary')
            : 'text-muted-foreground hover:text-foreground hover:bg-muted/20',
          hidden ? 'opacity-45' : '',
        ].join(' ')}
      >
        {vertical && pg.icon && <PageIcon name={pg.icon} className="w-3.5 h-3.5 shrink-0" />}
        {!vertical && pg.icon && <PageIcon name={pg.icon} className="w-3 h-3 shrink-0 inline mr-1 -mt-0.5" />}
        <span className="truncate" title={pg.description || ''}>{pg.name}</span>
        {hidden && <EyeOff className="w-3 h-3 shrink-0 ml-auto" />}
      </button>
    );
  };

  if (vertical) {
    // Grouped sidebar nav — pages sharing a `group` render under a section
    // header (first-appearance order); ungrouped pages render header-less.
    const order = [];
    const byGroup = {};
    pages.forEach((pg) => {
      const g = pg.group || '';
      if (!(g in byGroup)) { byGroup[g] = []; order.push(g); }
      byGroup[g].push(pg);
    });
    const namedGroups = order.filter(Boolean);
    const allCollapsed = namedGroups.length > 0 && namedGroups.every((g) => collapsed[g]);
    return (
      <div className="flex flex-col gap-1 w-44 shrink-0" data-testid="page-nav-bar">
        {namedGroups.length >= 2 && (
          <button
            type="button"
            onClick={() => setAllGroups(namedGroups, !allCollapsed)}
            className="flex items-center gap-1 self-start text-[9px] uppercase tracking-wider text-muted-foreground/60 hover:text-foreground px-2 py-0.5 mb-0.5 transition-colors"
            data-testid="page-nav-collapse-all"
          >
            {allCollapsed ? <ChevronsDownUp className="w-3 h-3 rotate-180" /> : <ChevronsDownUp className="w-3 h-3" />}
            {allCollapsed ? 'Expand all' : 'Collapse all'}
          </button>
        )}
        {order.map((g) => {
          const isCollapsed = g && collapsed[g];
          return (
            <div key={g || '__ungrouped'} className="flex flex-col gap-1">
              {g && (
                <button
                  type="button"
                  onClick={() => toggleGroup(g)}
                  aria-expanded={!isCollapsed}
                  className="flex items-center gap-1 w-full text-left text-[9px] uppercase tracking-wider text-muted-foreground/70 hover:text-foreground px-2 pt-2 pb-0.5 transition-colors"
                  data-testid={`page-nav-section-${g}`}
                >
                  <ChevronDown className={'w-3 h-3 shrink-0 transition-transform ' + (isCollapsed ? '-rotate-90' : '')} />
                  <span className="truncate">{g}</span>
                </button>
              )}
              {!isCollapsed && byGroup[g].map(renderBtn)}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 flex-wrap border-b border-border/30 pb-2 mb-3" data-testid="page-nav-bar">
      {pages.map(renderBtn)}
    </div>
  );
}

// Arranges the page navigation around the page content per the author's
// chosen style. `viewerMode` drops hidden pages entirely.
export function PageNavChrome({ style = 'top_tabs', pages = [], activeId, onChange, viewerMode = false, showHidden = false, storageKey = '', children }) {
  const narrow = useNarrow();
  const visible = useMemo(
    () => (viewerMode ? pages.filter(isPageVisible) : pages),
    [pages, viewerMode]
  );
  const effectiveStyle = narrow ? 'dropdown' : normalizeNavStyle(style);
  const showNav = visible.length > 1;

  if (!showNav) return <>{children}</>;

  // Subtle fade/slide when switching pages (viewer only — avoids remounting
  // the heavy builder grid on every author page switch).
  const content = viewerMode
    ? <div key={activeId} className="animate-in fade-in-0 slide-in-from-bottom-1 duration-300">{children}</div>
    : children;

  const nav = (
    <DashboardPageNav
      pages={visible}
      activeId={activeId}
      onChange={onChange}
      style={effectiveStyle}
      showHidden={showHidden && !viewerMode}
      storageKey={storageKey}
    />
  );

  if (effectiveStyle === 'left_sidebar' || effectiveStyle === 'right_sidebar') {
    return (
      <div className="flex gap-4 items-start" data-testid="page-nav-chrome">
        {effectiveStyle === 'left_sidebar' && nav}
        <div className="flex-1 min-w-0">{content}</div>
        {effectiveStyle === 'right_sidebar' && nav}
      </div>
    );
  }

  if (effectiveStyle === 'bottom_tabs') {
    return (
      <div className="flex flex-col" data-testid="page-nav-chrome">
        <div className="min-w-0">{content}</div>
        <div className="border-t border-border/30 pt-2 mt-3 flex items-center gap-1 flex-wrap" data-testid="page-nav-bottom">
          {visible.map((pg) => {
            const active = activeId === pid(pg);
            return (
              <button
                key={pid(pg)}
                onClick={() => onChange(pid(pg))}
                data-testid={`page-nav-item-${pid(pg)}`}
                className={'px-3 py-1.5 rounded-b-lg text-xs transition-colors ' + (active
                  ? 'bg-primary/15 text-primary font-semibold border-t-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/20')}
              >{pg.name}</button>
            );
          })}
        </div>
      </div>
    );
  }

  // top_tabs + dropdown
  return (
    <div className="flex flex-col" data-testid="page-nav-chrome">
      {nav}
      <div className="min-w-0">{content}</div>
    </div>
  );
}

// Author-only page management: add / rename / reorder (drag) / duplicate /
// delete / hide, plus the per-dashboard navigation-style picker.
export function PagesManager({ pages, activeId, navStyle, onSelect, onNavStyle, onAdd, onRename, onDuplicate, onDelete, onToggleHidden, onReorder, onSetIcon, onSetDescription, onSetGroup, onReorderGroups }) {
  const [dragIndex, setDragIndex] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const isSidebar = ['left_sidebar', 'right_sidebar'].includes(normalizeNavStyle(navStyle));
  const existingGroups = [...new Set((pages || []).map(p => p.group).filter(Boolean))];

  // Sections in first-appearance order (named groups + one implicit "Ungrouped"
  // block), matching how the sidebar nav renders them — used for reordering.
  const sectionOrder = [];
  (pages || []).forEach((p) => { const g = p.group || ''; if (!sectionOrder.includes(g)) sectionOrder.push(g); });

  return (
    <div className="space-y-4" data-testid="pages-manager">
      <div>
        <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 block">Navigation style</label>
        <Select value={normalizeNavStyle(navStyle)} onValueChange={onNavStyle}>
          <SelectTrigger className="h-8 text-xs rounded-lg bg-muted/40 border-border/50" data-testid="nav-style-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {NAV_STYLES.map((n) => <SelectItem key={n.value} value={n.value} className="text-xs">{n.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {onReorderGroups && isSidebar && sectionOrder.length > 1 && (
        <div data-testid="sections-manager">
          <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 block">Section order</label>
          <div className="space-y-1">
            {sectionOrder.map((g, i) => (
              <div key={g || '__ungrouped'} className="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-border/40 bg-muted/10" data-testid={`section-row-${g || 'ungrouped'}`}>
                <span className="flex flex-col -my-1 shrink-0">
                  <button
                    onClick={() => { if (i > 0) onReorderGroups(i, i - 1); }}
                    disabled={i === 0}
                    className="text-muted-foreground/60 hover:text-foreground disabled:opacity-25 disabled:cursor-default leading-none"
                    title="Move section up" data-testid={`section-moveup-${g || 'ungrouped'}`}
                  ><ChevronUp className="w-3 h-3" /></button>
                  <button
                    onClick={() => { if (i < sectionOrder.length - 1) onReorderGroups(i, i + 1); }}
                    disabled={i === sectionOrder.length - 1}
                    className="text-muted-foreground/60 hover:text-foreground disabled:opacity-25 disabled:cursor-default leading-none"
                    title="Move section down" data-testid={`section-movedown-${g || 'ungrouped'}`}
                  ><ChevronDown className="w-3 h-3" /></button>
                </span>
                <span className={'text-xs truncate ' + (g ? 'text-foreground' : 'text-muted-foreground italic')}>{g || 'Ungrouped'}</span>
              </div>
            ))}
          </div>
          <p className="text-[9px] text-muted-foreground/70 mt-1.5">Reorder whole sections at once. Pages keep their order within a section.</p>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Pages</label>
          <button onClick={onAdd} className="text-[10px] text-primary hover:underline flex items-center gap-1" data-testid="add-page-btn">
            <Plus className="w-3 h-3" /> Add page
          </button>
        </div>
        <div className="space-y-1">
          {pages.map((pg, i) => {
            const id = pid(pg);
            const hidden = !isPageVisible(pg);
            return (
              <div
                key={id}
                draggable
                onDragStart={() => setDragIndex(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragIndex !== null && dragIndex !== i) onReorder(dragIndex, i); setDragIndex(null); }}
                className={'rounded-lg border ' + (activeId === id ? 'bg-primary/10 border-primary/25' : 'border-border/40 hover:bg-muted/20')}
                data-testid={`page-row-${id}`}
              >
                <div onClick={() => onSelect(id)} className="flex items-center gap-1.5 px-2 py-1.5 cursor-pointer">
                  <GripVertical className="w-3 h-3 text-muted-foreground/60 cursor-grab shrink-0" />
                  <span className="flex flex-col -my-1 shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); if (i > 0) onReorder(i, i - 1); }}
                      disabled={i === 0}
                      className="text-muted-foreground/60 hover:text-foreground disabled:opacity-25 disabled:cursor-default leading-none"
                      title="Move up" data-testid={`page-moveup-${id}`}
                    ><ChevronUp className="w-3 h-3" /></button>
                    <button
                      onClick={(e) => { e.stopPropagation(); if (i < pages.length - 1) onReorder(i, i + 1); }}
                      disabled={i === pages.length - 1}
                      className="text-muted-foreground/60 hover:text-foreground disabled:opacity-25 disabled:cursor-default leading-none"
                      title="Move down" data-testid={`page-movedown-${id}`}
                    ><ChevronDown className="w-3 h-3" /></button>
                  </span>
                  {(onSetIcon) && (pg.icon ? <PageIcon name={pg.icon} className="w-3.5 h-3.5 shrink-0 text-muted-foreground" /> : <span className="w-3.5 h-3.5 shrink-0" />)}
                  <input
                    value={pg.name}
                    onChange={(e) => onRename(id, e.target.value)}
                    onFocus={() => onSelect(id)}
                    onClick={(e) => e.stopPropagation()}
                    className={'bg-transparent border-none outline-none flex-1 text-xs min-w-0 ' + (hidden ? 'text-muted-foreground line-through' : 'text-foreground')}
                    data-testid={`page-rename-${id}`}
                  />
                  <button
                    onClick={(e) => { e.stopPropagation(); onToggleHidden(id); }}
                    title={hidden ? 'Hidden from viewers — click to show' : 'Visible to viewers — click to hide'}
                    className={'p-1 rounded hover:bg-muted/40 ' + (hidden ? 'text-amber-400' : 'text-muted-foreground hover:text-foreground')}
                    data-testid={`page-hide-${id}`}
                  >
                    {hidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDuplicate(id); }}
                    title="Duplicate page"
                    className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/40"
                    data-testid={`page-duplicate-${id}`}
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                  {pages.length > 1 && (
                    confirmId === id ? (
                      <span className="flex items-center gap-1" data-testid={`page-delete-confirm-${id}`}>
                        <button
                          onClick={(e) => { e.stopPropagation(); onDelete(id); setConfirmId(null); }}
                          className="px-1.5 py-0.5 rounded text-[10px] bg-destructive text-destructive-foreground hover:opacity-90"
                          data-testid={`page-delete-yes-${id}`}
                        >Delete</button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmId(null); }}
                          className="px-1.5 py-0.5 rounded text-[10px] text-muted-foreground hover:bg-muted/40"
                          data-testid={`page-delete-no-${id}`}
                        >Cancel</button>
                      </span>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmId(id); }}
                        title="Delete page"
                        className="p-1 rounded text-muted-foreground/70 hover:text-destructive hover:bg-destructive/10"
                        data-testid={`page-delete-${id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )
                  )}
                </div>
                {onSetIcon && activeId === id && (
                  <div className="px-2 pb-2 pt-0.5 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-wrap gap-1 items-center" data-testid={`page-icons-${id}`}>
                      <button
                        onClick={() => onSetIcon(id, '')}
                        className={'px-1.5 py-1 rounded text-[9px] ' + (!pg.icon ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground')}
                        data-testid={`page-icon-${id}-none`}
                        title="No icon"
                      >None</button>
                      {PAGE_ICON_KEYS.map((k) => (
                        <button
                          key={k}
                          onClick={() => onSetIcon(id, k)}
                          className={'p-1 rounded ' + (pg.icon === k ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground')}
                          data-testid={`page-icon-${id}-${k}`}
                          title={k}
                        >
                          <PageIcon name={k} className="w-3.5 h-3.5" />
                        </button>
                      ))}
                    </div>
                    <input
                      value={pg.description || ''}
                      onChange={(e) => onSetDescription(id, e.target.value)}
                      placeholder="Short description (optional)"
                      className="w-full bg-muted/30 border border-border/40 rounded px-2 py-1 text-[11px] outline-none focus:border-primary/40"
                      data-testid={`page-desc-${id}`}
                    />
                    {onSetGroup && isSidebar && (
                      <>
                        <input
                          value={pg.group || ''}
                          onChange={(e) => onSetGroup(id, e.target.value)}
                          placeholder="Section (optional) — groups pages in the sidebar"
                          list={`page-groups-${id}`}
                          className="w-full bg-muted/30 border border-border/40 rounded px-2 py-1 text-[11px] outline-none focus:border-primary/40"
                          data-testid={`page-group-${id}`}
                        />
                        <datalist id={`page-groups-${id}`}>
                          {existingGroups.map(g => <option key={g} value={g} />)}
                        </datalist>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-[9px] text-muted-foreground/70 mt-1.5">Drag to reorder. The first visible page is where viewers land.</p>
      </div>
    </div>
  );
}
